import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { Tooltip, Divider } from "@material-ui/core";
import {
  ViewStream as CompactIcon,
  ViewModule as ComfortableIcon,
  ViewAgenda as DetailedIcon,
  Dashboard as RichIcon,
  List as DiscreteIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  group: {
    "& .MuiToggleButton-root": {
      padding: "4px 8px",
      border: "1px solid rgba(0,0,0,0.12)",
      "& svg": { fontSize: 18 },
    },
    "& .Mui-selected": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.12)"
          : "rgba(0,0,0,0.08)",
    },
  },
  divider: {
    height: 24,
    margin: "0 2px",
  },
}));

/**
 * ViewModeToggle — alterna entre 3 modos de visualização e 2 estilos de card.
 *
 * View modes (tamanho dos cards):
 *  - compact   : avatar, nome, tempo, SLA dot
 *  - comfortable: +lastMessage, +queue, +assignee, +tags
 *  - detailed  : +wallet, +connection, +valor, +priority
 *
 * Card styles (aparência):
 *  - rich    : card com SLA pulse, ícones de prioridade, chips coloridos (chamativo)
 *  - discrete: estilo lista, compacto como o card de ticket na sidebar (discreto)
 *
 * Props:
 *  - viewMode: string ("compact" | "comfortable" | "detailed")
 *  - cardStyle: string ("rich" | "discrete")
 *  - onChange: (newMode) => void
 *  - onCardStyleChange: (newStyle) => void
 */
const ViewModeToggle = ({
  viewMode = "comfortable",
  cardStyle = "rich",
  onChange,
  onCardStyleChange,
}) => {
  const classes = useStyles();

  const handleModeChange = (_event, newMode) => {
    if (newMode !== null) {
      onChange(newMode);
    }
  };

  const handleStyleChange = (_event, newStyle) => {
    if (newStyle !== null && onCardStyleChange) {
      onCardStyleChange(newStyle);
    }
  };

  return (
    <div className={classes.wrapper}>
      {/* Card style toggle */}
      <ToggleButtonGroup
        className={classes.group}
        value={cardStyle}
        exclusive
        onChange={handleStyleChange}
        size="small"
      >
        <ToggleButton value="rich">
          <Tooltip title="Chamativo">
            <RichIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="discrete">
          <Tooltip title="Discreto">
            <DiscreteIcon />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem className={classes.divider} />

      {/* View mode toggle */}
      <ToggleButtonGroup
        className={classes.group}
        value={viewMode}
        exclusive
        onChange={handleModeChange}
        size="small"
      >
        <ToggleButton value="compact">
          <Tooltip title="Compacto">
            <CompactIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="comfortable">
          <Tooltip title="Confortável">
            <ComfortableIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="detailed">
          <Tooltip title="Detalhado">
            <DetailedIcon />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </div>
  );
};

export default React.memo(ViewModeToggle);
